/**
 * Composition State Hook
 *
 * Manages wizard state for creating composite Smart Assets.
 *
 * @see https://gitlab.com/smart-assets.io/smart-asset-primitives/gitlab-profile/-/blob/master/docs/SmartAssetSpec/SA-SPEC-007-Composition.md
 */
import { useState, useMemo, useCallback } from "react";
import type {
  CompositionLink,
  CompositionGateConfig,
  CompositeSmartAsset,
} from "../types/valueFlows";
import type { SmartAssetData } from "../components/specific/SmartAssetsComponents/SmartAssetCard";

export type CompositionWizardStep =
  | "select" // Step 1: Select assets to compose
  | "wire" // Step 2: Wire interfaces between assets
  | "gates" // Step 3: Configure gates (optional)
  | "preview" // Step 4: Preview composite
  | "create"; // Step 5: Name and create

export interface CompositionState {
  /** Current wizard step */
  step: CompositionWizardStep;
  /** IDs of selected Smart Assets for composition */
  selectedAssetIds: string[];
  /** Wiring links between asset interfaces */
  links: CompositionLink[];
  /** Gate configurations keyed by link ID */
  gateConfigs: Record<string, CompositionGateConfig>;
  /** Name for the new composite */
  compositeName: string;
}

export interface UseCompositionStateReturn {
  /** Current state */
  state: CompositionState;

  // Step navigation
  /** Current step */
  step: CompositionWizardStep;
  /** Go to next step */
  nextStep: () => void;
  /** Go to previous step */
  prevStep: () => void;
  /** Go to specific step */
  goToStep: (step: CompositionWizardStep) => void;
  /** Check if can proceed to next step */
  canProceed: boolean;

  // Asset selection (Step 1)
  /** Selected asset IDs */
  selectedAssetIds: string[];
  /** Toggle asset selection */
  toggleAsset: (assetId: string) => void;
  /** Check if asset is selected */
  isAssetSelected: (assetId: string) => boolean;

  // Wiring (Step 2)
  /** Current composition links */
  links: CompositionLink[];
  /** Add a composition link */
  addLink: (link: Omit<CompositionLink, "id">) => void;
  /** Remove a composition link */
  removeLink: (linkId: string) => void;
  /** Update a composition link */
  updateLink: (linkId: string, updates: Partial<CompositionLink>) => void;

  // Gates (Step 3)
  /** Gate configurations */
  gateConfigs: Record<string, CompositionGateConfig>;
  /** Set gate config for a link */
  setGateConfig: (linkId: string, config: CompositionGateConfig | undefined) => void;

  // Composite creation (Step 5)
  /** Composite name */
  compositeName: string;
  /** Set composite name */
  setCompositeName: (name: string) => void;
  /** Create the composite (returns new composite or null on error) */
  createComposite: () => CompositeSmartAsset | null;

  // Reset
  /** Reset all state */
  reset: () => void;
}

const STEP_ORDER: CompositionWizardStep[] = ["select", "wire", "gates", "preview", "create"];

const initialState: CompositionState = {
  step: "select",
  selectedAssetIds: [],
  links: [],
  gateConfigs: {},
  compositeName: "",
};

/**
 * Generate a unique ID for new links
 */
function generateLinkId(): string {
  return `link-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Generate a mock RGB identity for a new composite
 */
function generateCompositeId(): string {
  const random = Math.random().toString(36).substr(2, 40);
  return `RGB:bc1p${random.padEnd(40, "0").substring(0, 40)}`;
}

/**
 * Hook for managing composition wizard state
 */
export function useCompositionState(
  availableAssets: SmartAssetData[] = [],
): UseCompositionStateReturn {
  const [state, setState] = useState<CompositionState>(initialState);

  // Step navigation
  const nextStep = useCallback(() => {
    setState((prev) => {
      const currentIndex = STEP_ORDER.indexOf(prev.step);
      if (currentIndex < STEP_ORDER.length - 1) {
        return { ...prev, step: STEP_ORDER[currentIndex + 1] };
      }
      return prev;
    });
  }, []);

  const prevStep = useCallback(() => {
    setState((prev) => {
      const currentIndex = STEP_ORDER.indexOf(prev.step);
      if (currentIndex > 0) {
        return { ...prev, step: STEP_ORDER[currentIndex - 1] };
      }
      return prev;
    });
  }, []);

  const goToStep = useCallback((step: CompositionWizardStep) => {
    setState((prev) => ({ ...prev, step }));
  }, []);

  // Check if can proceed to next step
  const canProceed = useMemo(() => {
    switch (state.step) {
      case "select":
        // Need at least 2 assets selected
        return state.selectedAssetIds.length >= 2;
      case "wire":
        // Need at least 1 link
        return state.links.length >= 1;
      case "gates":
        // Gates are optional, can always proceed
        return true;
      case "preview":
        // Can always proceed from preview
        return true;
      case "create":
        // Need a name
        return state.compositeName.trim().length > 0;
      default:
        return false;
    }
  }, [state.step, state.selectedAssetIds, state.links, state.compositeName]);

  // Asset selection
  const toggleAsset = useCallback((assetId: string) => {
    setState((prev) => {
      const isSelected = prev.selectedAssetIds.includes(assetId);
      return {
        ...prev,
        selectedAssetIds: isSelected
          ? prev.selectedAssetIds.filter((id) => id !== assetId)
          : [...prev.selectedAssetIds, assetId],
      };
    });
  }, []);

  const isAssetSelected = useCallback(
    (assetId: string) => {
      return state.selectedAssetIds.includes(assetId);
    },
    [state.selectedAssetIds],
  );

  // Wiring
  const addLink = useCallback((link: Omit<CompositionLink, "id">) => {
    setState((prev) => ({
      ...prev,
      links: [...prev.links, { ...link, id: generateLinkId() }],
    }));
  }, []);

  const removeLink = useCallback((linkId: string) => {
    setState((prev) => ({
      ...prev,
      links: prev.links.filter((l) => l.id !== linkId),
      // Also remove any gate config for this link
      gateConfigs: Object.fromEntries(
        Object.entries(prev.gateConfigs).filter(([id]) => id !== linkId),
      ),
    }));
  }, []);

  const updateLink = useCallback((linkId: string, updates: Partial<CompositionLink>) => {
    setState((prev) => ({
      ...prev,
      links: prev.links.map((l) => (l.id === linkId ? { ...l, ...updates } : l)),
    }));
  }, []);

  // Gates
  const setGateConfig = useCallback((linkId: string, config: CompositionGateConfig | undefined) => {
    setState((prev) => {
      if (config === undefined) {
        const { [linkId]: _, ...rest } = prev.gateConfigs;
        return { ...prev, gateConfigs: rest };
      }
      return {
        ...prev,
        gateConfigs: { ...prev.gateConfigs, [linkId]: config },
      };
    });
  }, []);

  // Composite creation
  const setCompositeName = useCallback((name: string) => {
    setState((prev) => ({ ...prev, compositeName: name }));
  }, []);

  const createComposite = useCallback((): CompositeSmartAsset | null => {
    if (state.selectedAssetIds.length < 2 || state.compositeName.trim().length === 0) {
      return null;
    }

    // Attach gate configs to links
    const linksWithGates = state.links.map((link) => ({
      ...link,
      gate_config: state.gateConfigs[link.id],
    }));

    const composite: CompositeSmartAsset = {
      id: generateCompositeId(),
      type: "composite",
      name: state.compositeName.trim(),
      created_at: new Date().toISOString(),
      components: [...state.selectedAssetIds],
      composition_links: linksWithGates,
    };

    return composite;
  }, [state.selectedAssetIds, state.links, state.gateConfigs, state.compositeName]);

  // Reset
  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  return {
    state,
    step: state.step,
    nextStep,
    prevStep,
    goToStep,
    canProceed,
    selectedAssetIds: state.selectedAssetIds,
    toggleAsset,
    isAssetSelected,
    links: state.links,
    addLink,
    removeLink,
    updateLink,
    gateConfigs: state.gateConfigs,
    setGateConfig,
    compositeName: state.compositeName,
    setCompositeName,
    createComposite,
    reset,
  };
}
